import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { LeftMenu, User } from "../../shared/models/base-response.model";
import { EventDetailsResponseModel } from "../../features/home/home.model";

/**
 * @interface User
 * @description Represents the structure of a user object.
 * @property {string} name - The name of the user.
 * @property {string} profileImg - The URL of the user's profile image.
 */


/**
 * @description
 * The `UserService` is a singleton service that manages the state of the currently logged-in user.
 * It provides methods to set, clear, retrieve, and update the user information.
 * The service uses an RxJS `BehaviorSubject` to maintain the user state and allows other components
 * to subscribe to changes in the user data.
 */
@Injectable({
  providedIn: "root",
})
export class UserService {
  /**
   * @description A private `BehaviorSubject` that holds the current user state.
   * Initialized with `null` to indicate no user is set initially.
   */
  private userSubject = new BehaviorSubject<User | null>(null);

  /**
   * @description An observable stream of the current user state.
   * Components can subscribe to this to react to user state changes.
   */
  user$ = this.userSubject.asObservable();

  userHasVirtualPass$ = new BehaviorSubject<boolean>(false);

  userHasFilledUserCategory$ = new BehaviorSubject<boolean>(false);

  
  private leftMenu = new BehaviorSubject<LeftMenu | null>(null);

  leftMenu$ = this.leftMenu.asObservable();

  /**
   * @description Sets the current user state.
   * @param {User} user - The user object to set as the current user.
   */
  setUser(user: User) {
    this.userSubject.next(user);
  }

  /**
   * @description Clears the current user state by setting it to `null`.
   */
  clearUser() {
    this.userSubject.next(null);
    this.userHasVirtualPass$.next(false);
    this.userHasFilledUserCategory$.next(false);
    this.leftMenu.next(null);
  }

  /**
   * @description Retrieves the current user state synchronously.
   * @returns {User | null} The current user object or `null` if no user is set.
   */
  getUser() {
    return this.userSubject.value;
  }

  /**
   * @description Updates the current user state with partial user data.
   * Merges the provided partial data with the existing user data.
   * @param {Partial<User>} partial - An object containing the partial user data to update.
   * @throws {Error} Throws an error if no user is currently set.
   */
  updateUser(partial: Partial<User>) {
    const currentUser = this.userSubject.value;

    if (!currentUser) {
      throw new Error("Cannot update user: no current user is set.");
    }

    const updatedUser = { ...currentUser, ...partial };
    this.setUser(updatedUser);
  }

  setHasVirtualPass(hasPass: boolean) {
    this.userHasVirtualPass$.next(hasPass);
  }

  setHasFilledUserCategory(hasFilled: boolean) {
    this.userHasFilledUserCategory$.next(hasFilled);
  }

  initializeFromEventStatus(userEventStatus: any): void {
    const userProfileImage = userEventStatus.user_profile.image_url
      ?? "/assets/images/Default-profile-photo.png";
    const userName = `${userEventStatus.user_profile.first_name} ${userEventStatus.user_profile.last_name}`;

    const hasUnassigned = userEventStatus.event_my_tickets.unassigned_tickets_bought_by_me > 0;
    const hasAssigned = userEventStatus.event_my_tickets.assigned_tickets_bought_by_me > 0
      || userEventStatus.event_my_tickets.tickets_assigned_for_me > 0;

    this.setUser({
      name: userName,
      profileImg: userProfileImage,
      mobile: userEventStatus.user_profile.mobile,
      state: userEventStatus.user_profile.state,
      city: userEventStatus.user_profile.city,
      hasUnassignedTickets: hasUnassigned,
      hasAssignedTickets: hasAssigned,
      // include other needed props
    });
  }


  setLeftMenu(leftMenu: LeftMenu) {
    this.leftMenu.next(leftMenu);
  }

  getLeftMenu() {
    return this.leftMenu.value;
  }

}
